import type { DataContext, MetadataTypeCollection } from "@octopusdeploy/octopus-server-client";
import { OctopusError } from "@octopusdeploy/octopus-server-client";
import type React from "react";
import { useEffect, useState } from "react";
import MetadataTypeValidator from "~/components/DynamicForm/MetadataTypeValidator";
export function validateIntegrationSettings(types: MetadataTypeCollection, model: DataContext): void {
    const validator = new MetadataTypeValidator();
    const validations = validator.validateType(types.Types, model);
    if (validations && validations.length > 0) {
        const exception = new OctopusError(0, "There were validation errors:");
        exception.Errors = validations;
        throw exception;
    }
}
export function getConnectivityTestUrl(metadata: MetadataTypeCollection | undefined, resourceTypeName: string, propertyName: string): string | undefined {
    const connectionResource = metadata?.Types?.find((type) => type.Name === resourceTypeName);
    const property = connectionResource?.Properties?.find((prop) => prop.Name === propertyName);
    return property?.DisplayInfo?.ConnectivityCheck?.Url;
}
export function useConnectionDrawer<TConnection>() {
    const [isConnectionDrawerOpen, setIsConnectionDrawerOpen] = useState<boolean>(false);
    const [selectedConnection, setSelectedConnection] = useState<TConnection | undefined>();
    const [selectedConnectionIndex, setSelectedConnectionIndex] = useState<number | undefined>();
    const openConnectionDrawer = (connection: TConnection | undefined, connections: TConnection[]) => {
        const connectionIndex = connection ? connections.findIndex((c) => c === connection) : undefined;
        setSelectedConnection(connection);
        setSelectedConnectionIndex(connectionIndex);
        setIsConnectionDrawerOpen(true);
    };
    const closeConnectionDrawer = () => {
        setSelectedConnection(undefined);
        setSelectedConnectionIndex(undefined);
        setIsConnectionDrawerOpen(false);
    };
    const createAddConnection = <TModel extends {
        Connections: TConnection[];
    }>(setModel: React.Dispatch<React.SetStateAction<TModel>>) => (connection: TConnection) => {
        if (selectedConnectionIndex === undefined) {
            // Add new connection
            setModel((prev) => ({
                ...prev,
                Connections: [...(prev.Connections ?? []), connection],
            }));
        }
        else {
            // Update existing connection
            setModel((prev) => ({
                ...prev,
                Connections: prev.Connections.map((c, index) => (index === selectedConnectionIndex ? connection : c)),
            }));
        }
        closeConnectionDrawer();
    };
    const createRemoveConnection = <TModel extends {
        Connections: TConnection[];
    }>(setModel: React.Dispatch<React.SetStateAction<TModel>>) => (connection: TConnection) => setModel((prev) => ({
        ...prev,
        Connections: prev.Connections.filter((c) => c !== connection),
    }));
    return {
        isConnectionDrawerOpen,
        selectedConnection,
        isEdit: selectedConnection !== undefined,
        openConnectionDrawer,
        closeConnectionDrawer,
        createAddConnection,
        createRemoveConnection,
    };
}
export function useConnectivityTestUrl(types: MetadataTypeCollection | undefined, resourceTypeName: string, propertyName: string) {
    const [connectivityTestUrl, setConnectivityTestUrl] = useState<string | undefined>();
    useEffect(() => {
        const url = getConnectivityTestUrl(types, resourceTypeName, propertyName);
        setConnectivityTestUrl(url);
    }, [types, resourceTypeName, propertyName]);
    return connectivityTestUrl;
}
